'use client';

import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';

const PINK = '#F2A9A9';

// global-error replaces the root layout, so it owns <html>/<body>. It must render even when JS
// chunks fail to load (common during deploys — the prime trigger for global errors), so it stays
// self-contained: inline styles + static pink frame blocks, with NO imports of the branded
// error-screen chain (CenteredErrorScreen/PixelReveal/GlitchButton) that would add chunk-load risk.
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry?.captureException?.(error, {
      level: 'fatal',
      tags: { errorBoundary: 'global' },
      contexts: { react: { componentStack: error.stack } },
    });
  }, [error]);

  return (
    <html>
      <body style={{ margin: 0 }}>
        <div
          data-testid="global-error-page"
          style={{
            minHeight: '100vh',
            display: 'grid',
            gridTemplateColumns: '15% 70% 15%',
            gridTemplateRows: 'minmax(2rem, 1fr) auto minmax(2rem, 1fr)',
            backgroundColor: '#FCFAF6',
            color: '#151414',
            fontFamily:
              'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
          }}
        >
          {/* Static pink frame (the branded CenteredErrorScreen look), inlined as plain divs at the
              grid edge-midpoints — no PixelReveal/import — so it renders even when chunks fail. */}
          <div aria-hidden style={{ gridColumn: '2', gridRow: '1', backgroundColor: PINK }} />
          <div aria-hidden style={{ gridColumn: '1', gridRow: '2', backgroundColor: PINK }} />
          <div aria-hidden style={{ gridColumn: '3', gridRow: '2', backgroundColor: PINK }} />
          <div aria-hidden style={{ gridColumn: '2', gridRow: '3', backgroundColor: PINK }} />
          <main
            style={{
              gridColumn: '2',
              gridRow: '2',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1.5rem',
              padding: '2.5rem 1.5rem',
              textAlign: 'center',
            }}
          >
            <h1
              style={{
                margin: 0,
                maxWidth: '20ch',
                fontFamily: 'ui-serif, Georgia, "Times New Roman", serif',
                fontSize: '2.5rem',
                fontWeight: 300,
                lineHeight: 1.1,
              }}
            >
              Something went wrong
            </h1>
            <p
              data-testid="error-description"
              style={{
                margin: 0,
                maxWidth: '44ch',
                fontSize: '1.125rem',
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              {error.message ||
                'An unexpected error occurred while loading this page.'}
            </p>
            {error.digest ? (
              <p
                style={{
                  margin: 0,
                  fontSize: '0.8125rem',
                  color: 'rgba(21, 20, 20, 0.5)',
                }}
              >
                Reference: {error.digest}
              </p>
            ) : null}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.75rem',
                justifyContent: 'center',
              }}
            >
              <button
                type="button"
                onClick={reset}
                data-testid="error-retry"
                style={{
                  padding: '0.75rem 1.25rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#FDFFF8',
                  backgroundColor: '#151414',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Try Again
              </button>
              {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- intentional full-document navigation: global-error must not depend on next/link's client router, which may be the very thing that broke */}
              <a
                href="/login"
                data-testid="login-back-button"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0.75rem 1.25rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#151414',
                  textDecoration: 'underline',
                }}
              >
                Return to login
              </a>
            </div>
          </main>
        </div>
      </body>
    </html>
  );
}
